/* @flow */

/**
 * Liferay requests service
 * The HTTP requests are handled using https://github.github.io/fetch
 */
import base64 from 'base-64';

import keys from 'config/keys';
import storageService from 'services/storage';

import areas from './areas';
import broadcastings from './broadcastings';
import broadcastTypes from './broadcastTypes';
import cart from './cart';
import categories from './categories';
import channels from './channels';
import medias from './medias';
import producers from './producers';
import productions from './productions';
import productionTypes from './productionTypes';
import territories from './territories';
import tracks from './tracks';
import usageTypes from './usageTypes';
import usages from './usages';
import worklists from './worklists';
import smartlists from './smartlists';
import featured from './featured';
import purchases from './purchases';
import onDemand from './onDemand';

import utils from './utils';
import domUtils from '../../utils/dom';

import type { User, Endpoint } from 'types';

//$FlowFixMe
export const LIFERAY_API_ENDPOINT = `${keys.LIFERAY_URL}/api/jsonws/invoke`;
/**
 * Utility function that calls the liferay endpoint.
 * @param {String} endpoint - Liferay endpoint.
 * @param {Object} params - API call params.
 * @param {String} username - Username used for the basic authentication.
 * @param {String} password - Password used for the basic authentication.
 * @return {Object} The body of the call response.
 */
export const callLiferayEndpoint = async (
  endpoint: Endpoint,
  params: any = {},
  username: ?string,
  password: ?string
) => {
  const bodyJSON = {};
  bodyJSON[endpoint] = params;
  const storedUser = JSON.parse(storageService.getFromStorage('user'));
  const authorization =
    username && password
      ? `Basic ${base64.encode(`${username}:${password}`)}`
      : `Basic ${base64.encode(`${storedUser.username}:${storedUser.password}`)}`;
  const response = await fetch(LIFERAY_API_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Authorization: authorization,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(bodyJSON),
  });
  if (!response.ok) {
    throw new Error(response.statusText || 'Connection error');
  }
  const responseBody = await response.json();
  if (responseBody.exception) {
    if (responseBody.message === 'Authenticated access required') {
      await logout();
      domUtils.refreshCurrentPage();
    }
    throw utils.toError(responseBody);
  }
  return responseBody;
};

/**
 * Checks if there is an user session saved in localstorage.
 * @return {?User} The user obtained from the localstorage.
 */
const initialize = async (): Promise<?User> => {
  const user = storageService.getFromStorage('user');
  return user ? JSON.parse(user) : undefined;
};

/**
 * Given an username and a password authenticates an user on liferay.
 * @param {String} username - Username used for the basic authentication.
 * @param {String} password - Password used for the basic authentication.
 * @return {User} The authenticated user.
 */
// const login = async (username: string, password: string): Promise<User> => {
//   await callLiferayEndpoint('/protones-portlet.supporti/news', {}, username, password);
//   const user = {
//     username,
//     password,
//   };
//   storageService.setInStorage('user', JSON.stringify(user));
//   return user;
// };

const login = async (username: string, password: string): Promise<User> => {
  await fetch(
    `${keys.LIFERAY_URL}/c/portal/login?p_p_id=58&p_p_lifecycle=1&p_p_state=normal&p_p_mode=view&p_p_col_id=column-1&p_p_col_count=1&_58_struts_action=%2Flogin%2Flogin`,
    {
      credentials: 'include',
      headers: {},
      referrer: `${keys.LIFERAY_URL}`,
      referrerPolicy: 'no-referrer-when-downgrade',
      body: `_58_formDate=${Date.now()}&_58_saveLastPath=false&_58_redirect=&_58_doActionAfterLogin=false&_58_login=${encodeURI(
        username
      )}&_58_password=${encodeURI(password)}&_58_rememberMe=false`,
      method: 'POST',
      mode: 'cors',
    }
  );
  const user = {
    username,
    password,
  };
  storageService.setInStorage('user', JSON.stringify(user));
  // HACK: check if we really are authenticated. A failed request will produce a logout() call
  await callLiferayEndpoint('/protones-portlet.usermanagement/is-utilizzatore');
  return user;
};

/**
 * Logout from liferay.
 */
// const logout = async () => {
//   storageService.clear();
//   // The following info shouldn't be cleared
//   storageService.setInStorage('reduxPersistReducerVersion', keys.REDUX_PERSIST_REDUCER_VERSION);
//   const cookies = document.cookie.split(';');
//   for (let i = 0; i < cookies.length; i++) {
//     const cookie = cookies[i];
//     const eqPos = cookie.indexOf('=');
//     const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
//     document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
//   }
//   return true;
// };

const logout = async () => {
  await fetch(`${keys.LIFERAY_URL}/c/portal/logout`, {
    credentials: 'include',
    headers: {},
    referrer: `${keys.LIFERAY_URL}/web/guest/home`,
    referrerPolicy: 'no-referrer-when-downgrade',
    body: null,
    method: 'GET',
    mode: 'cors',
  });
  storageService.clear();
  // The following info shouldn't be cleared
  storageService.setInStorage('reduxPersistReducerVersion', keys.REDUX_PERSIST_REDUCER_VERSION);
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
  }
  return true;
};

export default {
  initialize,
  login,
  logout,
  tracks,
  medias,
  categories,
  smartlists,
  featured,
  usages,
  worklists,
  producers,
  productions,
  cart,
  territories,
  usageTypes,
  areas,
  channels,
  broadcastings,
  broadcastTypes,
  productionTypes,
  purchases,
  onDemand,
};
